import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import Swal from "sweetalert2";
import { api } from "../../../../api/Middleware";
import { CALENDAR_URL } from "../../../../api/Urls";

/**
 * Modal de arquivamento de agenda.
 * Carrega preview de eventos futuros via GET /calendar/{id}/preview-archive/.
 * Se houver futuros, exige escolha de agenda destino antes de confirmar.
 */
const ArchiveCalendarModal = ({ show, onHide, calendar, onArchived }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !calendar?.id) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    setTransferTo("");
    api
      .get(`${CALENDAR_URL}${calendar.id}/preview-archive/`)
      .then((resp) => setPreview(resp.data))
      .catch((err) => {
        setError(err.response?.data?.error || "Erro ao carregar dados da agenda.");
      })
      .finally(() => setLoading(false));
  }, [show, calendar?.id]);

  const handleConfirm = async () => {
    if (!calendar?.id) return;
    const futureCount = preview?.future_events_count || 0;
    if (futureCount > 0 && !transferTo) {
      Swal.fire("Atenção", "Selecione uma agenda de destino para transferir os agendamentos futuros.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const body = futureCount > 0 ? { transfer_to_calendar_id: parseInt(transferTo, 10) } : {};
      const resp = await api.post(`${CALENDAR_URL}${calendar.id}/archive/`, body);
      onHide();
      Swal.fire({
        title: "Agenda arquivada",
        text: resp.data?.transferred_count
          ? `${resp.data.transferred_count} agendamento(s) transferido(s).`
          : "Agenda arquivada com sucesso.",
        icon: "success",
        timer: 2500,
      });
      onArchived?.();
    } catch (err) {
      Swal.fire(
        "Erro",
        err.response?.data?.error || "Não foi possível arquivar a agenda.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop={submitting ? "static" : true}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Arquivar agenda</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Carregando…
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        {preview && !loading && (
          <>
            <p>
              Você está arquivando a agenda <strong>{preview.calendar?.name}</strong>.
            </p>
            <Alert variant="info" className="small mb-3">
              Os agendamentos já realizados continuarão no banco para histórico.
              A agenda some do agente e da visualização do calendário.
              Você pode reativar a qualquer momento.
            </Alert>

            {(preview.dependent_agents || []).length > 0 && (
              <Alert variant="danger" className="small mb-3">
                <strong>⚠️ Atenção:</strong> esta agenda está vinculada ao(s) agente(s):
                <ul className="mb-2 mt-1">
                  {preview.dependent_agents.map((ag) => (
                    <li key={ag.id}><strong>{ag.name}</strong></li>
                  ))}
                </ul>
                Após arquivar, edite o nó de Agendamento desse(s) agente(s) e selecione
                outra agenda — senão o agente ficará sem agenda configurada.
              </Alert>
            )}

            {preview.future_events_count > 0 ? (
              <>
                <Alert variant="warning">
                  Esta agenda tem <strong>{preview.future_events_count}</strong> agendamento(s) futuro(s).
                  É necessário transferi-los para outra agenda antes de arquivar.
                </Alert>
                <Form.Group className="mb-3">
                  <Form.Label>Transferir para:</Form.Label>
                  <Form.Select
                    value={transferTo}
                    onChange={(e) => setTransferTo(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">Selecione uma agenda…</option>
                    {(preview.available_calendars || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                  {(preview.available_calendars || []).length === 0 && (
                    <Form.Text className="text-danger">
                      Não há outras agendas ativas. Crie uma nova agenda antes de arquivar esta.
                    </Form.Text>
                  )}
                </Form.Group>
              </>
            ) : (
              <p className="text-muted small">
                Não há agendamentos futuros. A agenda será arquivada imediatamente.
              </p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="warning"
          onClick={handleConfirm}
          disabled={submitting || loading || !!error}
        >
          {submitting ? <><Spinner animation="border" size="sm" /> Arquivando…</> : "Arquivar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ArchiveCalendarModal;
